import EventBus from 'eventing-bus';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, PlayArrow as PlayIcon, Stop as StopIcon, Build as BuildIcon } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, Box, Card, CardContent, TextField, Button, IconButton, Chip, Select, MenuItem, FormControl, List, ListItem, ListItemText, CircularProgress, Typography, Switch, FormControlLabel, Alert } from '@mui/material';

// Import Redux actions
import { getAllMCPServers, createMCPServer, updateMCPServer, deleteMCPServer, connectMCPServer, disconnectMCPServer, executeMCPTool, setToolExecutionResult } from '../../store/Assistant/index';

const MCPManager = ({ open, onClose }) => {
  const dispatch = useDispatch();

  // Redux state
  const { mcpServers, isLoading, mcpError, toolExecutionResult } = useSelector(state => state['Assistant']);

  // Local state
  const [toolArgs, setToolArgs] = useState({});
  const [isToolShow, setIsToolShow] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [editingServer, setEditingServer] = useState(null);
  const [serverDialogOpen, setServerDialogOpen] = useState(false);
  const [testToolDialogOpen, setTestToolDialogOpen] = useState(false);

  const [envVars, setEnvVars] = useState([{ key: '', value: '' }]);
  const [formData, setFormData] = useState({ name: '', transport: { type: 'stdio', command: '', args: [], env: {} }, status: 'active', serverPrompt: '' });

  // Load data on mount
  useEffect(() => {
    if (open) dispatch(getAllMCPServers());
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!serverDialogOpen) {
      setEditingServer(null);
      setEnvVars([{ key: '', value: '' }]);
      setFormData({ name: '', transport: { type: 'stdio', command: '', args: [], env: {} }, status: 'active', serverPrompt: '' });
    }
  }, [serverDialogOpen]);

  // Server management functions
  const handleAddServer = () => {
    setEditingServer(null);
    setServerDialogOpen(true);
    setEnvVars([{ key: '', value: '' }]);
    setFormData({ name: '', transport: { type: 'stdio', command: '', args: [], env: {} }, status: 'active', serverPrompt: '' });
  };

  const handleEditServer = (server) => {
    setFormData(server);
    setEditingServer(server);
    // Convert env object to array for editing
    const envArray = Object.entries(server['transport']['env'] || {}).map(([key, value]) => ({ key, value }));
    setEnvVars(envArray.length > 0 ? envArray : [{ key: '', value: '' }]);
    setServerDialogOpen(true);
  };

  const handleSaveServer = () => {
    // Validate form
    if (!formData['name']) {
      EventBus.publish('error', 'Server name is required');
      return;
    }

    if (formData['transport']['type'] === 'stdio' && !formData['transport']['command']) {
      EventBus.publish('error', 'Command is required for stdio transport');
      return;
    }

    if (formData['transport']['type'] === 'sse' && !formData['transport']['url']) {
      EventBus.publish('error', 'URL is required for SSE transport');
      return;
    }

    // Convert env array back to object
    const envObject = {};
    envVars.forEach(({ key, value }) => { if (key && value) envObject[key] = value; });
    const serverData = { ...formData, transport: { ...formData['transport'], env: envObject } };
    const action = editingServer
      ? updateMCPServer(editingServer['name'], serverData)
      : createMCPServer(serverData);

    // Close dialog on action dispatch
    setServerDialogOpen(false);
    dispatch(action);
  };

  const handleDeleteServer = (serverName) => {
    if (window.confirm(`Delete server ${serverName}?`)) dispatch(deleteMCPServer(serverName));
  };

  const handleToggleConnection = (server) => {
    const action = server['connected'] ? disconnectMCPServer(server['name']) : connectMCPServer(server['name']);
    dispatch(action);
  };

  // Tool management functions
  const handleTestTool = (tool) => {
    setSelectedTool(tool);
    setToolArgs({});
    setTestToolDialogOpen(true);
  };

  const handleExecuteTool = () => {
    if (!selectedTool) return;

    // Validate required parameters
    const schema = selectedTool['inputSchema'];
    const requiredFields = schema?.['required'] || [];
    const missingFields = requiredFields.filter(field => !toolArgs[field] || toolArgs[field].toString().trim() === '');
    
    if (missingFields.length > 0) {
      EventBus.publish('error', `Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Convert types based on schema
    const convertedArgs = {};
    const properties = schema?.['properties'] || {};
    
    Object.entries(toolArgs).forEach(([key, value]) => {
      const prop = properties[key];
      if (!prop || !value) {
        convertedArgs[key] = value;
        return;
      }

      try {
        const type = prop.type;
        const stringValue = value.toString().trim();

        switch (type) {
          case 'number':
          case 'integer':
            convertedArgs[key] = parseFloat(stringValue);
            if (isNaN(convertedArgs[key])) {
              throw new Error(`Invalid number format for ${key}: ${stringValue}`);
            }
            break;
            
          case 'boolean':
            convertedArgs[key] = stringValue.toLowerCase() === 'true' || stringValue === '1';
            break;
            
          case 'object':
            try {
              // Special handling for auth parameters - auto-format simple tokens
              if (key.toLowerCase().includes('auth') && !stringValue.startsWith('{')) {
                // If user entered a simple token, auto-format as auth object
                convertedArgs[key] = {
                  type: 'apiKey',
                  key: stringValue  // Use 'key' field for apiKey type
                };
              } else {
                convertedArgs[key] = JSON.parse(stringValue);
              }
            } catch (e) {
              throw new Error(`Invalid JSON format for ${key}: ${e.message}`);
            }
            break;
            
          case 'array':
            try {
              convertedArgs[key] = JSON.parse(stringValue);
              if (!Array.isArray(convertedArgs[key])) {
                throw new Error(`Expected array for ${key}`);
              }
            } catch (e) {
              throw new Error(`Invalid array format for ${key}: ${e.message}`);
            }
            break;
            
          default:
            convertedArgs[key] = stringValue;
        }
      } catch (error) {
        EventBus.publish('error', `Parameter conversion error: ${error.message}`);
        return;
      }
    });

    // Show execution start message
    EventBus.publish('info', `Executing ${selectedTool['displayName'] || selectedTool['name']} on ${selectedTool['server']}...`);

    // Debug log the converted arguments
    console.log('Converted arguments for MCP tool:', convertedArgs);

    // Clear previous results
    dispatch(setToolExecutionResult(null));

    // Execute the tool with converted arguments
    dispatch(executeMCPTool(selectedTool['server'], selectedTool['name'], convertedArgs));
  };

  // Environment variable management
  const handleAddEnvVar = () => { setEnvVars([...envVars, { key: '', value: '' }]); };
  const handleRemoveEnvVar = (index) => { setEnvVars(envVars.filter((_, i) => i !== index)); };
  const handleEnvVarChange = (index, field, value) => {
    const newEnvVars = [...envVars];
    newEnvVars[index][field] = value;
    setEnvVars(newEnvVars);
  };

  // Render helpers
  const renderServerCard = (server) => {
    const serverTools = server['tools'] ? server['tools'] : []
    return (
      <Card key={server['name']} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box flex={1}>
              <Typography variant="h6">{server['name']}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {server['transport']['type'].toUpperCase()} Transport
                {server['transport']['type'] === 'stdio' && ` • ${server['transport']['command']}`}
                {server['transport']['type'] === 'sse' && ` • ${server['transport']['url']}`}
              </Typography>

              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Chip label={server['connected'] ? 'Connected' : 'Disconnected'} color={server['connected'] ? 'success' : 'default'} size="small" />
                {server['status'] !== 'default' && (
                  <Chip label={server['status'] === 'active' ? 'Enabled' : 'Disabled'} color={server['status'] === 'active' ? 'primary' : 'default'} size="small" />
                )}
                {server['status'] === 'default' && <Chip label="Enabled" color="primary" size="small" />}
                {serverTools.length > 0 && (<button className="show-tools-btn" onClick={() => setIsToolShow(!isToolShow)}><Chip size="small" icon={<BuildIcon fontSize="small" />} label={<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}> {`${serverTools.length} tools`}<i className="icon fa fa-angle-down" style={{transform: isToolShow ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease',}}/></span>}/></button>)}
              </Box>
              {server['serverPrompt'] && <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}> "{server['serverPrompt']}" </Typography>}
            </Box>

            <Box display="flex" gap={1}>
              {server['status'] !== 'default' && (  
                <>
                  <IconButton onClick={() => handleToggleConnection(server)} color={server['connected'] ? 'error' : 'primary'} title={server['connected'] ? 'Disconnect' : 'Connect'}>{server['connected'] ? <StopIcon /> : <PlayIcon />}</IconButton>
                  <IconButton onClick={() => handleEditServer(server)} title="Edit server"><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDeleteServer(server['name'])} color="error" title="Delete server"><DeleteIcon /></IconButton>
                </>
              )}
            </Box>
            {isToolShow && (
              <div className='group-form accrodian-box'>
                {/* <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: 'left' }}>Browse and test tools from your connected MCP servers</Typography> */}
                {serverTools.length > 0 ? (
                  <Box>
                    <Typography sx={{ flexShrink: 0, fontWeight: 'medium', p: 1, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                      Browse and test tools from your connected MCP servers
                    </Typography>
                    <Box sx={{ overflow: 'auto'}}>
                      <List sx={{ width: '100%' }}>
                        {serverTools.map(tool => (
                          <ListItem key={tool['name']}>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}> {tool['displayName'] || tool['name']}</Typography>
                                  {tool['customized'] && <Chip label="Customized" size="small" color="secondary" />}
                                </Box>
                              }
                              secondary={
                                <Typography variant="body2" color="textSecondary" sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}> 
                                  {tool['description'] || 'No description available'} 
                                </Typography>
                              }
                            />
                            <Button size="small" variant="outlined" onClick={() => handleTestTool(tool)} sx={{ minWidth: '70px' }}>
                              Test
                            </Button>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ marginLeft: '20px' }}>No tools available for this server.</Alert>
                )}
              </div>
            )}
          </Box>
        </CardContent>
      </Card>
    )
  }

  const renderToolParameters = () => {
    if (!selectedTool || !selectedTool['inputSchema']) return null;

    const schema = selectedTool['inputSchema'];
    const properties = schema['properties'] || {};

    return Object.entries(properties).map(([key, prop]) => {
      const isRequired = schema['required']?.includes(key);
      const paramType = prop.type;
      
      return (
        <div key={key} className='group-form'>
          <label>
            {key}
            {isRequired && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
            <Chip 
              size="small" 
              variant="outlined"
              sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
              label={paramType === 'object' && key.toLowerCase().includes('auth') ? 'auth' : paramType}
              color={key.toLowerCase().includes('auth') ? 'success' : paramType === 'object' || paramType === 'array' ? 'primary' : 'default'}
            />
          </label>
          <TextField
            fullWidth
            margin="normal"
            required={isRequired}
            value={toolArgs[key] || ''}
            onChange={(e) => setToolArgs({ ...toolArgs, [key]: e['target']['value'] })}
            multiline={paramType === 'object' && !key.toLowerCase().includes('auth') || paramType === 'array'}
            rows={paramType === 'object' && !key.toLowerCase().includes('auth') || paramType === 'array' ? 3 : 1}
            helperText={
              <span>
                {prop['description'] || `Enter ${key} value`}
                {paramType === 'object' && key.toLowerCase().includes('auth') && <span style={{ color: '#2196f3', fontStyle: 'italic' }}> (Just enter your API token - auto-formatted)</span>}
                {paramType === 'object' && !key.toLowerCase().includes('auth') && <span style={{ color: '#666', fontStyle: 'italic' }}> (JSON format required)</span>}
                {paramType === 'array' && <span style={{ color: '#666', fontStyle: 'italic' }}> (JSON array format required)</span>}
                {paramType === 'boolean' && <span style={{ color: '#666', fontStyle: 'italic' }}> (true/false)</span>}
                {(paramType === 'number' || paramType === 'integer') && <span style={{ color: '#666', fontStyle: 'italic' }}> (numeric value)</span>}
              </span>
            }
          />
        </div>
      );
    });
  };

  return (
    <>
      {/* --------------- MCP Manager Modal--------------- */}
      <Modal isOpen={open} onClose={onClose} className={`main-modal new-user-modal mcp-modal modal-dialog-centered`}>
        <ModalHeader toggle={onClose}></ModalHeader>
        <ModalBody>
          <div className='mcp-manager-content'>
            <div className='top-area-add-server'>
              <div className='left-area'>
                <h3>MCP Manager</h3>
                <p>{mcpServers.length} server{mcpServers.length !== 1 ? 's' : ''} configured</p>
              </div>
              <div className='right-area'>
                <button className='btn-style-border' onClick={handleAddServer}><AddIcon /> Add Server</button>
              </div>
            </div>
            <div className='bottom-server-area'>
              {isLoading ? 
                <Box className="server-confige-box" display="flex" justifyContent="center" py={4}><CircularProgress /></Box> : mcpError ? 
                <Alert severity="error">{mcpError}</Alert> : mcpServers && mcpServers.length > 0 ? 
                mcpServers.map((server) => renderServerCard(server)) : <Alert severity="info">No MCP servers configured</Alert>
              }
            </div>
          </div>
        </ModalBody>
      </Modal>


      {/* Server Form Dialog */}
      <Dialog className='server-add' open={serverDialogOpen} onClose={() => setServerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingServer ? 'Edit Server' : 'Add MCP Server'}</DialogTitle>
        <DialogContent>
          <div className='group-form'>
            <label>Server Name</label>
            <TextField
              fullWidth
              margin="normal"
              value={formData['name']}
              disabled={!!editingServer}
              placeholder="e.g., filesystem-tools"
              helperText="A unique name to identify this server"
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div className='group-form'>
            <label>Transport Type</label>
            <FormControl fullWidth margin="normal">
              <Select value={formData['transport']['type']} onChange={(e) => setFormData({ ...formData, transport: { ...formData['transport'], type: e.target.value } })}>
                <MenuItem value="stdio">Standard I/O (Local Process)</MenuItem>
                <MenuItem value="sse">Server-Sent Events (Remote)</MenuItem>
              </Select>
            </FormControl>
          </div>


          {formData['transport']['type'] === 'stdio' && (
            <>
              <div className='group-form'>
                <label>Command</label>
                <TextField
                  fullWidth
                  margin="normal"
                  placeholder="e.g., npx, python, node"
                  value={formData['transport']['command']}
                  helperText="The command to run the MCP server"
                  onChange={(e) => setFormData({ ...formData, transport: { ...formData['transport'], command: e.target.value } })}
                />
              </div>
              <div className='group-form'>
                <label>Arguments (comma-separated)</label>
                <TextField
                  fullWidth
                  margin="normal"
                  helperText="Command line arguments"
                  value={formData['transport']['args']?.join(', ') || ''}
                  placeholder="e.g., -y, @modelcontextprotocol/server-filesystem, /tmp"
                  onChange={(e) => setFormData({ ...formData, transport: { ...formData['transport'], args: e.target.value.split(',').map(arg => arg.trim()).filter(Boolean) } })}
                />
              </div>
              <div className='group-form'>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Environment Variables</Typography>
                {envVars.map((envVar, index) => (
                  <Box key={index} display="flex" gap={1} mb={1}>
                    <TextField placeholder="Key" value={envVar.key} onChange={(e) => handleEnvVarChange(index, 'key', e.target.value)} size="small" sx={{ flex: 1 }} />
                    <TextField placeholder="Value" value={envVar.value} onChange={(e) => handleEnvVarChange(index, 'value', e.target.value)} size="small" sx={{ flex: 1 }} />
                    <IconButton onClick={() => handleRemoveEnvVar(index)} size="small" disabled={envVars.length === 1}><DeleteIcon /></IconButton>
                  </Box>
                ))}
                <button className='btn-style-border style-small' onClick={handleAddEnvVar}><AddIcon /> Add Variable</button>
              </div>
            </>
          )}

          {formData['transport']['type'] === 'sse' && (
            <div className='group-form'>
              <label>SSE URL</label>
              <TextField
                fullWidth
                margin="normal"
                helperText="The SSE endpoint URL"
                placeholder="https://example.com/mcp/sse"
                value={formData['transport']['url'] || ''}
                onChange={(e) => setFormData({ ...formData, transport: { ...formData['transport'], url: e.target.value } })}
              />
            </div>
          )}

          <div className='group-form'>
            <label>Server Prompt (Optional)</label>
            <TextField
              rows={2}
              fullWidth
              multiline
              margin="normal"
              value={formData['serverPrompt'] || ''}
              placeholder="Custom instructions for this server"
              onChange={(e) => setFormData({ ...formData, serverPrompt: e.target.value })}
              helperText="Additional context or instructions when using tools from this server"
            />
          </div>
          <div className='group-form'>
            <FormControlLabel control={<Switch checked={formData['status'] === 'active'} onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })} />} label="Enable server on startup" sx={{ mt: 2 }} />
          </div>
          <div className='group-form btn-groups'>
            <button className='btn-style-border' onClick={() => setServerDialogOpen(false)}>Cancel</button>
            <button className='btn-style-new' onClick={handleSaveServer}>{editingServer ? 'Update' : 'Create'}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Tool Dialog */}
      <Dialog className='server-add' open={testToolDialogOpen} onClose={() => { setTestToolDialogOpen(false); dispatch(setToolExecutionResult(null)); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}><BuildIcon />Test Tool: {selectedTool?.['name']}</Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{selectedTool?.['description']}</Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Parameters:</Typography>
            {renderToolParameters()}
            {(!selectedTool?.['inputSchema'] || Object.keys(selectedTool['inputSchema']['properties'] || {}).length === 0) &&
              <Typography variant="body2" color="textSecondary">This tool requires no parameters</Typography>
            }
          </Box>

          {toolExecutionResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Result:
                {toolExecutionResult.error ? (
                  <Chip label="Error" color="error" size="small" sx={{ ml: 1 }} />
                ) : (
                  <Chip label="Success" color="success" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: toolExecutionResult.error ? 'error.light' : 'grey.100', 
                borderRadius: 1, 
                maxHeight: 300, 
                overflow: 'auto',
                border: toolExecutionResult.error ? '1px solid' : 'none',
                borderColor: toolExecutionResult.error ? 'error.main' : 'transparent'
              }}>
                {toolExecutionResult.error ? (
                  <Typography color="error" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {toolExecutionResult.error}
                  </Typography>
                ) : (
                  <pre style={{ 
                    margin: 0, 
                    whiteSpace: 'pre-wrap', 
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }}>
                    {typeof toolExecutionResult === 'string' 
                      ? toolExecutionResult 
                      : JSON.stringify(toolExecutionResult, null, 2)
                    }
                  </pre>
                )}
              </Box>
              {!toolExecutionResult.error && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Tool executed successfully at {new Date().toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          )}
          <div className='group-form btn-groups'>
            <button className='btn-style-border' onClick={() => { setTestToolDialogOpen(false); dispatch(setToolExecutionResult(null)); }}>Close</button>
            <button 
              className='btn-style-new' 
              onClick={handleExecuteTool} 
              disabled={isLoading}
              style={{ 
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                  Executing...
                </>
              ) : (
                'Execute'
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MCPManager;